import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';

// Note: Using Times-Roman which has better Unicode support for ₹ symbol than Helvetica

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 40,
    paddingBottom: 120, // Extra padding at bottom for footer
    fontSize: 10,
    fontFamily: 'Times-Roman', // Times-Roman has better Unicode support for ₹ symbol
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottom: '2 solid #000000',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flex: 1,
    textAlign: 'right',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  logo: {
    width: 50,
    height: 50,
    marginRight: 10,
    objectFit: 'contain',
  },
  clinicName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  clinicSubtitle: {
    fontSize: 9,
    color: '#000000',
  },
  headerText: {
    fontSize: 9,
    marginBottom: 3,
    color: '#000000',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 15,
    paddingBottom: 5,
    borderBottom: '1 solid #cccccc',
    color: '#000000',
  },
  sectionContent: {
    marginBottom: 15,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  label: {
    fontWeight: 'bold',
    width: '40%',
    color: '#000000',
    fontSize: 9,
  },
  value: {
    width: '60%',
    color: '#000000',
  },
  table: {
    width: '100%',
    marginTop: 10,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #dddddd',
    paddingVertical: 6,
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    paddingVertical: 8,
  },
  tableCell: {
    paddingHorizontal: 8,
    fontSize: 9,
    color: '#000000',
  },
  tableCellDate: {
    width: '18%',
  },
  tableCellProcedure: {
    width: '25%',
  },
  tableCellNextAppt: {
    width: '18%',
  },
  tableCellAmount: {
    width: '13%',
    textAlign: 'right',
  },
  tableFooter: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    paddingVertical: 8,
  },
  gridContainer: {
    marginTop: 10,
  },
  gridRow: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  gridCell: {
    flex: 1,
    padding: 3,
    border: '0.5 solid #dddddd',
    textAlign: 'center',
    fontSize: 8,
  },
  note: {
    fontSize: 9,
    marginTop: 5,
    color: '#666666',
    fontStyle: 'italic',
  },
  twoColumnRow: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  twoColumnLeft: {
    width: '50%',
    paddingRight: 15,
  },
  twoColumnRight: {
    width: '50%',
    paddingLeft: 15,
  },
  medicalHistoryRow: {
    flexDirection: 'row',
    marginBottom: 6,
    width: '100%',
  },
  medicalHistoryLabel: {
    fontWeight: 'bold',
    width: '45%',
    color: '#000000',
    fontSize: 9,
  },
  medicalHistoryValue: {
    width: '55%',
    color: '#000000',
    fontSize: 9,
  },
  tagContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 3,
  },
  tag: {
    backgroundColor: '#f0f0f0',
    border: '1 solid #cccccc',
    borderRadius: 3,
    padding: '2 6',
    marginRight: 4,
    marginBottom: 4,
    fontSize: 8,
  },
  dentalGridContainer: {
    marginTop: 10,
    marginBottom: 10,
  },
  dentalGridLabel: {
    fontSize: 8,
    color: '#666666',
    marginBottom: 3,
  },
  dentalGridRow: {
    flexDirection: 'row',
    marginBottom: 2,
    width: '100%',
  },
  dentalToothColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 5,
  },
  dentalStatusText: {
    fontSize: 7,
    color: '#000000',
    marginBottom: 2,
    minHeight: 10,
    textAlign: 'center',
  },
  dentalGridCell: {
    flex: 1,
    padding: 4,
    border: '0.5 solid #dddddd',
    textAlign: 'center',
    fontSize: 8,
    minHeight: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  dentalGridCellNumber: {
    flex: 1,
    padding: 2,
    textAlign: 'center',
    fontSize: 7,
    color: '#666666',
    minHeight: 12,
  },
  fullWidthRow: {
    flexDirection: 'row',
    marginBottom: 5,
    width: '100%',
  },
  submittedBy: {
    marginTop: 20,
    marginBottom: 15,
    paddingTop: 10,
    borderTop: '1 solid #cccccc',
  },
  submittedByText: {
    fontSize: 10,
    color: '#000000',
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 8,
    color: '#666666',
    borderTop: '1 solid #dddddd',
    paddingTop: 10,
    marginTop: 20,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerSection: {
    width: '48%',
    marginBottom: 8,
  },
  footerTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 3,
  },
  footerText: {
    fontSize: 8,
    color: '#666666',
    marginBottom: 2,
  },
});

const PatientRecordPDF = ({ 
  patientInfo,
  appointmentDetails,
  medicalHistory,
  dentalExamination,
  procedures,
  profile
}) => {
  // Validate and ensure unique appointment ID
  // This ensures each PDF is associated with a unique appointment
  const appointmentId = appointmentDetails?.referenceId || appointmentDetails?.id || null;
  const appointmentNumericId = appointmentDetails?.id || null;
  
  // Generate unique document identifier to prevent overwrites
  // Combines appointment ID with timestamp for absolute uniqueness
  const documentId = appointmentId 
    ? `${appointmentId}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    : `DOC-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return 'Not specified';
    }
  };

  // Format currency - using "Rs." instead of ₹ since Times-Roman font doesn't support ₹ symbol properly
  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    // Format number with commas for thousands (Indian locale)
    const formattedNumber = num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    // Use "Rs." instead of ₹ to ensure proper rendering in PDF
    return `Rs. ${formattedNumber}`;
  };

  // Get procedure rows
  const procedureRows = procedures?.rows || procedures?.procedures || [];
  
  // Calculate totals
  const totals = {
    total: procedureRows.reduce((sum, r) => sum + Number(r.total || 0), 0),
    paid: procedureRows.reduce((sum, r) => sum + Number(r.paid || 0), 0),
    due: procedureRows.reduce((sum, r) => sum + Number(r.due || 0), 0),
  };

  // Get medical problems
  const getMedicalProblems = () => {
    if (!medicalHistory?.medical_problems) return [];
    try {
      const problems = typeof medicalHistory.medical_problems === 'string' 
        ? JSON.parse(medicalHistory.medical_problems) 
        : medicalHistory.medical_problems;
      return Array.isArray(problems) ? problems : [];
    } catch {
      return [];
    }
  };

  // Get dental findings
  const getDentalFindings = () => {
    if (!dentalExamination?.findings) return { upper: [], lower: [] };
    try {
      const findings = typeof dentalExamination.findings === 'string'
        ? JSON.parse(dentalExamination.findings)
        : dentalExamination.findings;
      return findings || { upper: [], lower: [] };
    } catch {
      return { upper: [], lower: [] };
    }
  };

  // Get trigger factors
  const getTriggerFactors = () => {
    if (!dentalExamination?.trigger_factors) return [];
    try {
      const factors = typeof dentalExamination.trigger_factors === 'string'
        ? JSON.parse(dentalExamination.trigger_factors)
        : dentalExamination.trigger_factors;
      return Array.isArray(factors) ? factors : [];
    } catch {
      return [];
    }
  };

  const findings = getDentalFindings();
  const medicalProblems = getMedicalProblems();
  const triggerFactors = getTriggerFactors();
  
  // Get patient type for dental findings
  const patientType = dentalExamination?.patient_type || "Adult";
  const isChild = patientType === "Child";
  
  // Tooth numbers based on patient type
  const adultTeethNumbers = [8, 7, 6, 5, 4, 3, 2, 1, 1, 2, 3, 4, 5, 6, 7, 8];
  const childTeethNumbers = ['E', 'D', 'C', 'B', 'A', 'A', 'B', 'C', 'D', 'E'];
  const teethNumbers = isChild ? childTeethNumbers : adultTeethNumbers;
  const gridSize = isChild ? 10 : 16;
  
  // Process findings arrays
  const upperFindings = findings?.upper || [];
  const lowerFindings = findings?.lower || [];
  
  // Create arrays for grades and status
  const upperGrades = Array(gridSize).fill("").map((_, idx) => 
    upperFindings[idx]?.grade || ""
  );
  const upperStatus = Array(gridSize).fill("").map((_, idx) => 
    upperFindings[idx]?.status || ""
  );
  const lowerGrades = Array(gridSize).fill("").map((_, idx) => 
    lowerFindings[idx]?.grade || ""
  );
  const lowerStatus = Array(gridSize).fill("").map((_, idx) => 
    lowerFindings[idx]?.status || ""
  );
  
  // Helper to abbreviate status
  const abbreviateStatus = (status) => {
    if (!status) return "";
    const statusLower = status.toLowerCase();
    if (statusLower === "caries") return "C";
    if (statusLower === "filled") return "F";
    if (statusLower === "missing") return "M";
    if (statusLower === "fractured") return "Fr";
    if (statusLower === "root canal treated") return "R";
    return status.charAt(0).toUpperCase();
  };
  
  // Helper to get grade color (for PDF, we'll use text styling)
  const getGradeStyle = (grade) => {
    if (!grade) return { color: '#999999' };
    const gradeNum = parseInt(grade);
    if (gradeNum >= 3) return { color: '#000000', fontWeight: 'bold' };
    if (gradeNum >= 2) return { color: '#333333' };
    return { color: '#666666' };
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <View style={styles.logoContainer}>
              <Image 
                src="/logo-print.png" 
                style={styles.logo}
              />
              <View>
                <Text style={styles.clinicName}>Gugan Dental Care</Text>
                <Text style={styles.clinicSubtitle}>GOBICHETTIPALAYAM</Text>
              </View>
            </View>
            <Text style={styles.headerText}>
              {profile?.clinic_name || profile?.address || "GRK Complex, Bawa Medical Upstairs, Near Bustand Signal"}
            </Text>
            <Text style={styles.headerText}>
              {profile?.clinic_name || profile?.address ? "" : "Gobichettipalayam – 638452"}
            </Text>
            <Text style={styles.headerText}>
              Phone: {profile?.phone || "+91 96299 96774"}
            </Text>
            <Text style={styles.headerText}>
              Email: {profile?.email || "gugandentalcare@gmail.com"}
            </Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.clinicName}>Patient Record</Text>
            <Text style={styles.headerText}>
              <Text style={{ fontWeight: 'bold' }}>Patient Name:</Text> {patientInfo?.name || "N/A"}
            </Text>
            <Text style={styles.headerText}>
              <Text style={{ fontWeight: 'bold' }}>Appointment ID:</Text> {appointmentId || "N/A"}
              {!appointmentId && (
                <Text style={{ color: '#ff0000', fontSize: 7, fontStyle: 'italic' }}> (WARNING: Missing Appointment ID)</Text>
              )}
            </Text>
            {appointmentNumericId && appointmentId !== appointmentNumericId.toString() && (
              <Text style={styles.headerText}>
                <Text style={{ fontWeight: 'bold' }}>Record ID:</Text> {appointmentNumericId}
              </Text>
            )}
            <Text style={styles.headerText}>
              <Text style={{ fontWeight: 'bold' }}>Phone:</Text> {patientInfo?.phone || "N/A"}
            </Text>
            <Text style={styles.headerText}>
              <Text style={{ fontWeight: 'bold' }}>Address:</Text> {patientInfo?.address || "N/A"}
            </Text>
            <Text style={styles.headerText}>
              <Text style={{ fontWeight: 'bold' }}>Date:</Text> {formatDate(new Date())}
            </Text>
          </View>
        </View>

        {/* Medical History Section */}
        <View style={styles.sectionContent}>
          <Text style={styles.sectionTitle}>Medical History</Text>
          
          <View style={styles.medicalHistoryRow}>
            <Text style={styles.medicalHistoryLabel}>Surgery/Hospitalization (last 5 years):</Text>
            <Text style={styles.medicalHistoryValue}>
              {medicalHistory?.surgery_hospitalized || "Not specified"}
              {medicalHistory?.surgery_hospitalized === 'Yes' && medicalHistory?.surgery_details
                ? ` — ${medicalHistory.surgery_details}`
                : ""}
            </Text>
          </View>

          <View style={styles.medicalHistoryRow}>
            <Text style={styles.medicalHistoryLabel}>Current Fever/Cold/Cough:</Text>
            <Text style={styles.medicalHistoryValue}>
              {medicalHistory?.fever_cold_cough || "Not specified"}
              {medicalHistory?.fever_cold_cough === 'Yes' && medicalHistory?.symptoms_details
                ? ` — ${medicalHistory.symptoms_details}`
                : ""}
            </Text>
          </View>

          <View style={styles.medicalHistoryRow}>
            <Text style={styles.medicalHistoryLabel}>Abnormal Bleeding (injury/operation):</Text>
            <Text style={styles.medicalHistoryValue}>
              {medicalHistory?.abnormal_bleeding || "Not specified"}
              {medicalHistory?.abnormal_bleeding === 'Yes' && medicalHistory?.abnormal_bleeding_details
                ? ` — ${medicalHistory.abnormal_bleeding_details}`
                : ""}
            </Text>
          </View>

          <View style={styles.medicalHistoryRow}>
            <Text style={styles.medicalHistoryLabel}>Currently Taking Medicine:</Text>
            <Text style={styles.medicalHistoryValue}>
              {medicalHistory?.taking_medicine || "Not specified"}
              {medicalHistory?.taking_medicine === 'Yes' && medicalHistory?.medicine_details
                ? ` — ${medicalHistory.medicine_details}`
                : ""}
            </Text>
          </View>

          <View style={styles.medicalHistoryRow}>
            <Text style={styles.medicalHistoryLabel}>Medication Allergy:</Text>
            <Text style={styles.medicalHistoryValue}>
              {medicalHistory?.allergic_medication || "Not specified"}
              {medicalHistory?.allergic_medication === 'Yes' && medicalHistory?.allergy_details
                ? ` — ${medicalHistory.allergy_details}`
                : ""}
            </Text>
          </View>

          <View style={styles.medicalHistoryRow}>
            <Text style={styles.medicalHistoryLabel}>Patient Type:</Text>
            <Text style={styles.medicalHistoryValue}>{medicalHistory?.patient_type || "Not specified"}</Text>
          </View>

          <View style={styles.fullWidthRow}>
            <Text style={styles.label}>Past Dental History:</Text>
            <Text style={styles.value}>{medicalHistory?.past_dental_history || "Not specified"}</Text>
          </View>

          <View style={styles.fullWidthRow}>
            <Text style={styles.label}>Medical Problems:</Text>
            <View style={styles.tagContainer}>
              {medicalProblems.length > 0 ? (
                medicalProblems.map((prob, i) => (
                  <Text key={i} style={styles.tag}>{prob}</Text>
                ))
              ) : (
                <Text style={styles.value}>No problems reported</Text>
              )}
            </View>
          </View>
        </View>

        {/* Dental Examination Section */}
        <View style={styles.sectionContent}>
          <Text style={styles.sectionTitle}>Dental Examination</Text>
          
          <View style={styles.twoColumnRow}>
            <View style={styles.twoColumnLeft}>
              <Text style={styles.label}>Chief Complaint:</Text>
              <Text style={styles.value}>{dentalExamination?.chief_complaint || "Not specified"}</Text>
            </View>
            <View style={styles.twoColumnRight}>
              <Text style={styles.label}>Duration & Onset:</Text>
              <Text style={styles.value}>{dentalExamination?.duration_onset || "Not specified"}</Text>
            </View>
          </View>

          <View style={styles.fullWidthRow}>
            <Text style={styles.label}>Trigger Factors:</Text>
            <View style={styles.tagContainer}>
              {triggerFactors.length > 0 ? (
                triggerFactors.map((factor, i) => (
                  <Text key={i} style={styles.tag}>{factor}</Text>
                ))
              ) : (
                <Text style={styles.value}>None reported</Text>
              )}
            </View>
          </View>

          {/* Dental Findings Grid */}
          <View style={styles.dentalGridContainer}>
            <Text style={[styles.sectionTitle, { fontSize: 12, marginBottom: 8 }]}>Dental Findings ({patientType})</Text>
            
            {/* Upper Jaw Section */}
            <Text style={styles.dentalGridLabel}>Upper (Right → Left)</Text>
            {/* Upper: Status above Grade */}
            <View style={styles.dentalGridRow}>
              {teethNumbers.map((num, idx) => (
                <View key={`upper-tooth-${idx}`} style={styles.dentalToothColumn}>
                  <Text style={styles.dentalStatusText}>
                    {abbreviateStatus(upperStatus[idx] || "")}
                  </Text>
                  <View style={styles.dentalGridCell}>
                    <Text style={getGradeStyle(upperGrades[idx] || "")}>
                      {upperGrades[idx] || "-"}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
            {/* Upper Tooth Numbers */}
            <View style={styles.dentalGridRow}>
              {teethNumbers.map((num, idx) => (
                <View key={`un-${idx}`} style={styles.dentalGridCellNumber}>
                  <Text style={{ fontSize: 7, color: '#666666' }}>{num}</Text>
                </View>
              ))}
            </View>
            
            {/* Lower Jaw Section */}
            <Text style={[styles.dentalGridLabel, { marginTop: 15 }]}>Lower (Right ← Left)</Text>
            {/* Lower: Numbers above Grade */}
            <View style={styles.dentalGridRow}>
              {teethNumbers.map((num, idx) => (
                <View key={`lower-tooth-${idx}`} style={styles.dentalToothColumn}>
                  <View style={styles.dentalGridCellNumber}>
                    <Text style={{ fontSize: 7, color: '#666666' }}>{num}</Text>
                  </View>
                  <View style={styles.dentalGridCell}>
                    <Text style={getGradeStyle(lowerGrades[idx] || "")}>
                      {lowerGrades[idx] || "-"}
                    </Text>
                  </View>
                  <Text style={styles.dentalStatusText}>
                    {abbreviateStatus(lowerStatus[idx] || "")}
                  </Text>
                </View>
              ))}
            </View>
            
            {/* Legend */}
            <View style={{ marginTop: 12 }}>
              <Text style={{ fontSize: 7, color: '#666666', marginBottom: 3, fontWeight: 'bold' }}>
                STATUS ABBREVIATIONS
              </Text>
              <Text style={{ fontSize: 7, color: '#666666', marginBottom: 5 }}>
                C — Caries | F — Filled | M — Missing | Fr — Fractured | R — Root Canal Treated | I — Intact | Cr — Crown
              </Text>
              <Text style={{ fontSize: 7, color: '#666666', marginBottom: 3, fontWeight: 'bold' }}>
                GRADE LEGEND
              </Text>
              <Text style={{ fontSize: 7, color: '#666666' }}>
                A — No issue | B — Mild | C — Moderate | D — Severe | E — Extensive
              </Text>
            </View>
          </View>

          <View style={styles.fullWidthRow}>
            <Text style={styles.label}>Diagnosis Notes:</Text>
            <Text style={styles.value}>{dentalExamination?.diagnosis_notes || "No diagnosis notes"}</Text>
          </View>

          <View style={styles.fullWidthRow}>
            <Text style={styles.label}>Treatment Plan Notes:</Text>
            <Text style={styles.value}>{dentalExamination?.treatment_plan_notes || "No treatment plan notes"}</Text>
          </View>
        </View>

        {/* Procedures & Payments Section */}
        <View style={styles.sectionContent}>
          <Text style={styles.sectionTitle}>Procedures & Payments</Text>
          
          {procedureRows.length > 0 ? (
            <View style={styles.table}>
              {/* Table Header */}
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.tableCell, styles.tableCellDate]}>Date</Text>
                <Text style={[styles.tableCell, styles.tableCellProcedure]}>Procedure</Text>
                <Text style={[styles.tableCell, styles.tableCellNextAppt]}>Next Appt</Text>
                <Text style={[styles.tableCell, styles.tableCellAmount]}>Total</Text>
                <Text style={[styles.tableCell, styles.tableCellAmount]}>Paid</Text>
                <Text style={[styles.tableCell, styles.tableCellAmount]}>Due</Text>
              </View>
              
              {/* Table Rows */}
              {procedureRows.map((row, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, styles.tableCellDate]}>
                    {formatDate(row.visitDate || row.visit_date)}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableCellProcedure]}>
                    {row.procedure || "Not specified"}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableCellNextAppt]}>
                    {formatDate(row.nextApptDate || row.next_appt_date)}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableCellAmount]}>
                    {formatCurrency(row.total)}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableCellAmount]}>
                    {formatCurrency(row.paid)}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableCellAmount]}>
                    {formatCurrency(row.due)}
                  </Text>
                </View>
              ))}
              
              {/* Table Footer */}
              <View style={[styles.tableRow, styles.tableFooter]}>
                <Text style={[styles.tableCell, { width: '61%', textAlign: 'right' }]}>Totals</Text>
                <Text style={[styles.tableCell, styles.tableCellAmount]}>
                  {formatCurrency(totals.total)}
                </Text>
                <Text style={[styles.tableCell, styles.tableCellAmount]}>
                  {formatCurrency(totals.paid)}
                </Text>
                <Text style={[styles.tableCell, styles.tableCellAmount]}>
                  {formatCurrency(totals.due)}
                </Text>
              </View>
            </View>
          ) : (
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>Not specified</Text>
              <Text style={styles.tableCell}>Not specified</Text>
              <Text style={styles.tableCell}>Not specified</Text>
              <Text style={[styles.tableCell, styles.tableCellAmount]}>{formatCurrency(0)}</Text>
              <Text style={[styles.tableCell, styles.tableCellAmount]}>{formatCurrency(0)}</Text>
              <Text style={[styles.tableCell, styles.tableCellAmount]}>{formatCurrency(0)}</Text>
            </View>
          )}
        </View>

        {/* Submitted By Section */}
        <View style={styles.submittedBy}>
          <Text style={styles.submittedByText}>
            Submitted By: Dr. {profile?.name || profile?.full_name || 'N/A'}
          </Text>
        </View>

        {/* Footer - appears on all pages */}
        <View style={styles.footer} fixed>
          <View style={styles.footerSection}>
            <Text style={styles.footerTitle}>Working Hours</Text>
            <Text style={styles.footerText}>Mon - Sat : 9:30 AM - 9:00 PM</Text>
            <Text style={styles.footerText}>Sunday : 10:00 AM - 7:00 PM</Text>
          </View>
          <View style={styles.footerSection}>
            <Text style={styles.footerTitle}>Our Location</Text>
            <Text style={styles.footerText}>
              <Text style={{ fontWeight: 'bold' }}>Main Branch:</Text> GRK complex, Bawa medical upstairs, Near Bustand signal, Gobichettipalayam - 638452
            </Text>
            <Text style={styles.footerText}>
              <Text style={{ fontWeight: 'bold' }}>Other Location:</Text> Vairam bricks complex, Vasthu Nagar, Saravana Theatre Rd, Gobichettipalayam - 638476
            </Text>
            {/* Unique Document ID - prevents overwrite confusion */}
            <Text style={{ fontSize: 6, color: '#999999', marginTop: 5, fontFamily: 'Courier' }}>
              Doc ID: {documentId}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PatientRecordPDF;

